import { CSSProperties } from 'react';

export const WA_PRESET_COLORS = [
  '#0E1333',  // Navy (brand)
  '#5271A5',  // Slate (brand)
  '#E7007D',  // Magenta (brand)
  '#ef4444',  // Red
  '#dc2626',  // Dark Red
  '#b91c1c',  // Crimson
  '#9f1239',  // Burgundy
  '#ec4899',  // Rose
  '#db2777',  // Dark Rose
  '#c026d3',  // Fuchsia
  '#a855f7',  // Violet
  '#8b5cf6',  // Purple
  '#7c3aed',  // Dark Purple
  '#6366f1',  // Indigo
  '#4f46e5',  // Deep Indigo
  '#3b82f6',  // Blue
  '#2563eb',  // Royal Blue
  '#1d4ed8',  // Dark Blue
  '#06b6d4',  // Cyan
  '#0891b2',  // Dark Cyan
  '#0e7490',  // Deep Teal
  '#14b8a6',  // Mint
  '#0d9488',  // Teal
  '#0f766e',  // Petrol
  '#22c55e',  // Green
  '#16a34a',  // Forest Green
  '#15803d',  // Dark Green
  '#84cc16',  // Lime
  '#65a30d',  // Olive Green
  '#a3a322',  // Olive Yellow
  '#facc15',  // Light Yellow
  '#eab308',  // Yellow
  '#f59e0b',  // Amber
  '#f97316',  // Orange
  '#ea580c',  // Dark Orange
  '#c2410c',  // Burnt Orange
];

/* ========================================
   Design Tokens (aligned to sundaeos.com)
   ======================================== */

const colors = {
  navy: '#0E1333',
  slate: '#5271A5',
  slateDark: '#3b5998',
  magenta: '#E7007D',

  textPrimary: '#0E1333',
  textBody: '#334155',
  textMuted: '#64748b',
  textLight: '#94a3b8',

  bgPage: '#f8fafc',
  bgCard: '#ffffff',
  bgMuted: '#f9fafb',
  bgHover: '#f1f5f9',

  border: '#e5e7eb',
  borderLight: '#f0f0f0',

  success: '#22c55e',
  successLight: '#dcfce7',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  danger: '#ef4444',
  dangerLight: '#fee2e2',
  info: '#3b82f6',
  infoLight: '#dbeafe',
};

const shadows = {
  card: '0 1px 3px rgba(0,0,0,0.06)',
  soft: '0 1px 2px rgba(0,0,0,0.05)',
  medium: '0 4px 12px rgba(0,0,0,0.08)',
  elevated: '0 8px 24px rgba(0,0,0,0.12)',
};

export { colors, shadows };

/* ========================================
   Component Styles
   ======================================== */

export const personalakteStyles = {
  pageContainer: {
    padding: 24,
    maxWidth: 1400,
    margin: '0 auto',
    minHeight: '100vh',
    background: colors.bgPage,
  } as CSSProperties,

  card: {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    padding: 28,
    boxShadow: shadows.card,
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  } as CSSProperties,

  cardCompact: {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    padding: 20,
    boxShadow: shadows.card,
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  } as CSSProperties,

  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 9999,
    cursor: 'pointer',
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    color: colors.textMuted,
    transition: 'all 0.2s ease',
    lineHeight: '20px',
  } as CSSProperties,

  buttonDisabled: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: colors.bgCard,
    color: colors.textLight,
    border: `1px solid ${colors.border}`,
    borderRadius: 9999,
    cursor: 'not-allowed',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '20px',
  } as CSSProperties,

  buttonPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 24px',
    background: 'linear-gradient(90deg, #0E1333 0%, #0E1333 30%, #E7007D 100%)',
    border: 'none',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    boxShadow: '0 2px 8px rgba(231, 0, 125, 0.15)',
    transition: 'all 0.2s ease',
    lineHeight: '20px',
  } as CSSProperties,

  buttonSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: colors.textMuted,
    transition: 'all 0.2s ease',
    lineHeight: '20px',
  } as CSSProperties,

  fieldWrapper: {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    padding: '8px 12px',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  } as CSSProperties,

  inputStyle: {
    width: '100%',
    padding: 0,
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
    outline: 'none',
  } as CSSProperties,

  labelStyle: {
    display: 'block',
    marginBottom: 2,
    fontSize: 11,
    fontWeight: 500,
    color: colors.textLight,
  } as CSSProperties,

  listItem: {
    padding: 12,
    borderRadius: 10,
    border: `1px solid ${colors.borderLight}`,
    transition: 'background 0.15s ease',
  } as CSSProperties,

  innerContainer: {
    padding: 12,
    background: colors.bgMuted,
    borderRadius: 8,
    border: `1px solid ${colors.borderLight}`,
  } as CSSProperties,

  actionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 20px',
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: colors.slate,
    lineHeight: '20px',
    transition: 'all 0.2s ease',
  } as CSSProperties,

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    fontWeight: 700,
    color: colors.navy,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    marginBottom: 12,
  } as CSSProperties,

  sectionDot: (color: string): CSSProperties => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  }),

  kpiValue: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.navy,
    lineHeight: 1.2,
  } as CSSProperties,

  kpiLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textMuted,
    marginBottom: 8,
  } as CSSProperties,

  badge: (bgColor: string, textColor: string): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 600,
    background: bgColor,
    color: textColor,
  }),

  tabButton: (isActive: boolean): CSSProperties => ({
    padding: '10px 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: isActive ? '2px solid #E7007D' : '2px solid transparent',
    borderRadius: 0,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? '#0E1333' : '#94a3b8',
    transition: 'all 0.2s',
  }),

  workAreaPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 14px',
    background: 'linear-gradient(90deg, #0E1333 0%, #0E1333 30%, #E7007D 100%)',
    color: '#fff',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
  } as CSSProperties,
};
